import { createContext, useCallback, useContext, useMemo } from 'react';

import { useStepsContext } from 'src/components/VacancyCreate/contexts/StepsContext';
import { StepName } from 'src/components/VacancyCreate/contexts/StepsContext/types';

interface CurrentStepContextValues {
    stepName: StepName | null;
    setToRenderedFieldsRef: (fieldName: string) => void;
    deleteFromRenderedFieldsRef: (fieldName: string) => void;
}

export const CurrentStepContext = createContext<CurrentStepContextValues>({
    stepName: null,
    setToRenderedFieldsRef: () => null,
    deleteFromRenderedFieldsRef: () => null,
});

export const useCurrentStepContext = (): CurrentStepContextValues =>
    useContext<CurrentStepContextValues>(CurrentStepContext);

interface CurrentStepContextProviderProps {
    stepName?: StepName;
}

const CurrentStepContextProvider: React.FC<CurrentStepContextProviderProps & React.PropsWithChildren> = ({
    stepName,
    children,
}) => {
    const { renderedFieldsRef } = useStepsContext();

    const setToRenderedFieldsRef = useCallback(
        (fieldName: string) => {
            if (stepName) {
                renderedFieldsRef.current.set(fieldName, { stepName });
            }
        },
        [renderedFieldsRef, stepName]
    );

    const deleteFromRenderedFieldsRef = useCallback(
        (fieldName: string) => {
            renderedFieldsRef.current.delete(fieldName);
        },
        [renderedFieldsRef]
    );

    const value = useMemo(
        () => ({
            stepName: stepName || null,
            setToRenderedFieldsRef,
            deleteFromRenderedFieldsRef,
        }),
        [deleteFromRenderedFieldsRef, setToRenderedFieldsRef, stepName]
    );

    return <CurrentStepContext.Provider value={value}>{children}</CurrentStepContext.Provider>;
};

export default CurrentStepContextProvider;
